/**
 * ☰ Admin › Data health — the fleet's DQHealth, two reports side by side.
 *
 * `services/qv-data/app/dq/fleet.py` produces one report per run: every machine
 * of qf-calc-us, qf-us, qf-scr-calc and qf-scr, every `.arrow` file on its
 * volume, and the last date for a fixed watchlist plus five random symbols drawn
 * once and sent to all of them. `docs/dq-health.html` is the design.
 *
 * ## Two reports, not a history
 *
 * Rpt 1 is the newest and Rpt 2 the one before it, side by side on every row,
 * because the question this screen answers is "did it move?" — a file that read
 * 09-16 in both is a different story from one that has just gone back a day.
 * Ten are kept per kind; the screen shows two and the rest are the record.
 *
 * ## The age is a finding
 *
 * A screen showing two healthy reports and no fresh one is a scheduler that
 * stopped, which is precisely the silence DQHealth exists to break — so each
 * column is headed by how old it is, not only when it was taken.
 *
 * ## It renders what it was given
 *
 * No verdicts are computed here. The levels, the comparison with the writer and
 * the "not trading" judgement are all the collector's, so this screen and the
 * `/dqhealth` page cannot disagree about what is wrong.
 */

import { useCallback, useEffect, useState } from "react";
import { plainFileName, plainText } from "@qufolio/screener-grid";

import {
  dqHealthConfigured,
  fetchReports,
  type FleetReport,
  type ReportKind,
  type StoredReport,
  type Verdict,
} from "../api/dqhealth";

const PANEL: React.CSSProperties = {
  minWidth: 720,
  maxWidth: 1180,
  maxHeight: "72vh",
  overflow: "auto",
  fontSize: "0.82rem",
  color: "var(--qf-text)",
};

const TABS: React.CSSProperties = {
  display: "flex",
  gap: 6,
  alignItems: "center",
  marginBottom: 10,
  flexWrap: "wrap",
};

const BUTTON: React.CSSProperties = {
  background: "var(--qf-surface)",
  color: "var(--qf-text)",
  border: "1px solid var(--qf-border)",
  borderRadius: 6,
  padding: "3px 10px",
  fontSize: "0.78rem",
  cursor: "pointer",
};

function tabStyle(active: boolean): React.CSSProperties {
  return active
    ? { ...BUTTON, background: "var(--qf-accent)", color: "var(--qf-surface)",
        // `border`, not `borderColor`: mixing the shorthand with a longhand
        // makes React warn on every re-render, and the tab re-renders on click.
        border: "1px solid transparent", fontWeight: 600 }
    : BUTTON;
}

const TABLE: React.CSSProperties = {
  borderCollapse: "collapse",
  width: "100%",
  fontVariantNumeric: "tabular-nums",
};

const TH: React.CSSProperties = {
  textAlign: "left",
  padding: "5px 8px",
  borderBottom: "1px solid var(--qf-border)",
  color: "var(--qf-text-secondary)",
  fontSize: "0.7rem",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  whiteSpace: "nowrap",
};

const TD: React.CSSProperties = {
  padding: "4px 8px",
  borderBottom: "1px solid var(--qf-border)",
  whiteSpace: "nowrap",
};

const SECTION: React.CSSProperties = {
  margin: "16px 0 6px",
  fontSize: "0.72rem",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "var(--qf-text-secondary)",
};

/** A verdict's colour. Only a finding is coloured; the rest stay quiet. */
const TONE: Record<string, string> = {
  ok: "var(--qf-positive)",
  // With a fallback, as `ChartTemplates` does: not every theme defines a
  // warning token, and an undefined one paints the text the body colour — the
  // amber row would then read exactly like the healthy ones.
  stale: "var(--qf-warning, #d29922)",
  ahead: "var(--qf-warning, #d29922)",
  absent: "var(--qf-warning, #d29922)",
  missing: "var(--qf-negative)",
};

/** How old, in the words a person uses. */
export function ago(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return "—";
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "—";
  const seconds = Math.max(0, (now - then) / 1000);
  if (seconds < 90) return `${Math.round(seconds)}s ago`;
  if (seconds < 5400) return `${Math.round(seconds / 60)}m ago`;
  if (seconds < 172800) return `${(seconds / 3600).toFixed(1)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

/** A verdict as a cell: the level, and the date it was judged on. */
function Cell({ verdict, date }: { verdict?: Verdict; date?: string | null }) {
  const level = verdict?.level ?? "none";
  if (level === "none" || !verdict) return <span>{date ?? "—"}</span>;
  if (level === "ok") return <span style={{ color: TONE.ok }}>{date ?? "ok"}</span>;
  // Every other level names itself **and** the date it was judged on: "stale"
  // alone sends somebody to another screen to find out how stale.
  const label = verdict.asOf ? `${levelText(level)} · ${verdict.asOf}` : levelText(level);
  return <span style={{ color: TONE[level] ?? "var(--qf-text-secondary)" }}>{label}</span>;
}

function levelText(level: string): string {
  if (level === "notTrading") return "not trading";
  if (level === "na") return "n/a";
  return level;
}

/** One report's column header: which run it was, and how old. */
function Head({ report, label }: { report?: StoredReport; label: string }) {
  if (!report) return <th style={TH}>{label} — none</th>;
  return (
    <th style={TH}>
      {label} · {ago(report.generatedAt)}
      <div style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>
        {report.trigger ?? "—"}
      </div>
    </th>
  );
}

function summaryLine(report: FleetReport | undefined): string {
  if (!report) return "—";
  const s = report.summary as Record<string, number>;
  const blocked = (report.summary.sourcesBlocked as string[] | undefined) ?? [];
  // **Asleep is not blocked.** `qv-pg` is up for the blocks that need it and
  // down for the rest of the day, so every morning beat names both markets —
  // as a finding it would be amber on every quiet morning.
  const asleep = (report.summary.sourcesUnreachable as string[] | undefined) ?? [];
  return [
    `${s.probed}/${s.machines} machines`,
    `stale ${s.stale}`,
    `missing ${s.missing}`,
    `mismatch ${s.mismatches}`,
    `not trading ${s.notTrading ?? 0}`,
    blocked.length
      ? `sources blocked: ${blocked.join(", ")}`
      : asleep.length
        ? `sources asleep: ${asleep.join(", ")}`
        : "sources ok",
  ].join(" · ");
}

/** Every machine named by either report, in the order the newest lists them. */
function machineKeys(reports: (FleetReport | undefined)[]): string[] {
  const keys: string[] = [];
  for (const report of reports) {
    for (const machine of report?.machines ?? []) {
      const key = `${machine.app}/${machine.id ?? "?"}`;
      if (!keys.includes(key)) keys.push(key);
    }
  }
  return keys;
}

function machineIn(report: FleetReport | undefined, key: string) {
  return report?.machines.find((m) => `${m.app}/${m.id ?? "?"}` === key);
}

export function DataHealthPanel() {
  const [kind, setKind] = useState<ReportKind>("eod");
  const [reports, setReports] = useState<StoredReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string | null>(null);

  const load = useCallback(
    (which: ReportKind) => {
      setLoading(true);
      void fetchReports(which, "US", 2).then((rows) => {
        setReports(rows);
        setLoading(false);
      });
    },
    [],
  );

  useEffect(() => load(kind), [kind, load]);

  const first = reports[0];
  const second = reports[1];
  const bodies = [first?.report, second?.report];
  const keys = machineKeys(bodies);
  const machineKey = selected && keys.includes(selected) ? selected : keys[0] ?? null;

  if (!dqHealthConfigured) {
    return <div style={PANEL}>No server configured, so there are no reports to show.</div>;
  }

  return (
    <div style={PANEL}>
      <div style={TABS}>
        <button type="button" style={tabStyle(kind === "eod")} onClick={() => setKind("eod")}>
          After a pass (EOD)
        </button>
        <button type="button" style={tabStyle(kind === "rth")} onClick={() => setKind("rth")}>
          Hourly beat (RTH)
        </button>
        <button type="button" style={BUTTON} onClick={() => load(kind)}>
          Refresh
        </button>
        <span style={{ color: "var(--qf-text-secondary)" }}>
          {loading
            ? "loading…"
            : first
              ? `expected session ${first.report.expectedSession ?? "—"} · drawn ${
                  first.report.random.join(", ") || "—"
                }`
              : ""}
        </span>
      </div>

      {!loading && reports.length === 0 && (
        // A fresh deploy has none, and a scheduler that stopped has none that
        // are fresh — this screen must be able to say the first without
        // pretending it is the second.
        <p>
          No {kind.toUpperCase()} report stored yet. The collector runs on qf-jobs&apos;
          schedule; <code>dqhealth-{kind}</code> writes one each time.
        </p>
      )}

      {reports.length > 0 && (
        <>
          <table style={TABLE}>
            <thead>
              <tr>
                <th style={TH}>summary</th>
                <Head report={first} label="Rpt 1" />
                <Head report={second} label="Rpt 2" />
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={TD}>overall</td>
                <td style={TD}>{summaryLine(first?.report)}</td>
                <td style={TD}>{summaryLine(second?.report)}</td>
              </tr>
              {["bars", "fundamentals"].map((store) =>
                Object.keys(first?.report.sources?.[store as "bars"] ?? {}).map((code) => (
                  <tr key={`${store}-${code}`}>
                    <td style={TD}>
                      Postgres · {store} {code}
                    </td>
                    {[first, second].map((r, i) => {
                      const info = r?.report.sources?.[store as "bars"]?.[code] as
                        | Record<string, string>
                        | undefined;
                      return (
                        <td style={TD} key={i}>
                          {info
                            ? `${info.state ?? (info.ok ? "ok" : "—")}${
                                info.newest ? ` · newest ${info.newest}` : ""
                              }`
                            : "—"}
                        </td>
                      );
                    })}
                  </tr>
                )),
              )}
            </tbody>
          </table>

          <div style={SECTION}>Machines</div>
          <div style={TABS}>
            {keys.map((key) => {
              const machine = machineIn(first?.report, key) ?? machineIn(second?.report, key);
              const levels = machine?.levels ?? {};
              const bad = (levels.missing ?? 0) + (levels.stale ?? 0) +
                (machine?.mismatches?.length ?? 0);
              return (
                <button
                  key={key}
                  type="button"
                  style={tabStyle(key === machineKey)}
                  onClick={() => setSelected(key)}
                >
                  {machine?.app ?? key}
                  {bad > 0 ? ` · ${bad}` : ""}
                  {machine?.probed === false ? " · unreachable" : ""}
                </button>
              );
            })}
          </div>

          {machineKey && (
            <MachineView
              machineKey={machineKey}
              first={first?.report}
              second={second?.report}
            />
          )}
        </>
      )}
    </div>
  );
}

function MachineView({
  machineKey,
  first,
  second,
}: {
  machineKey: string;
  first?: FleetReport;
  second?: FleetReport;
}) {
  const a = machineIn(first, machineKey);
  const b = machineIn(second, machineKey);
  const symbols = a?.report?.symbolFiles ?? b?.report?.symbolFiles ?? [];
  const paths: string[] = [];
  for (const machine of [a, b]) {
    for (const file of machine?.report?.files ?? []) {
      if (!paths.includes(file.path)) paths.push(file.path);
    }
  }

  const fileIn = (machine: typeof a, path: string) =>
    machine?.report?.files?.find((f) => f.path === path);
  const symbolIn = (machine: typeof a, symbol: string) =>
    machine?.report?.symbolFiles?.find((s) => s.symbol === symbol);

  return (
    <>
      {a?.report?.error && (
        <p style={{ color: "var(--qf-negative)" }}>{plainText(a.report.error)}</p>
      )}
      {(a?.mismatches?.length ?? 0) > 0 && (
        <>
          <div style={SECTION}>Disagrees with {first?.writerApp}</div>
          <table style={TABLE}>
            <tbody>
              {a?.mismatches?.map((m, i) => (
                <tr key={i}>
                  <td style={TD}>
                    {plainFileName(m.path)}
                    {m.symbol ? ` · ${m.symbol}` : ""}
                  </td>
                  <td style={TD}>writer {m.writer}</td>
                  <td style={{ ...TD, color: "var(--qf-negative)" }}>here {m.theirs}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {symbols.length > 0 && (
        <>
          <div style={SECTION}>Bar files — watchlist and random</div>
          <table style={TABLE}>
            <thead>
              <tr>
                <th style={TH}>symbol</th>
                <th style={TH}>Rpt 1</th>
                <th style={TH}>Rpt 2</th>
              </tr>
            </thead>
            <tbody>
              {symbols.map((row) => (
                <tr key={row.symbol}>
                  <td style={TD}>{row.symbol}</td>
                  <td style={TD}>
                    <Cell verdict={symbolIn(a, row.symbol)?.verdict}
                          date={symbolIn(a, row.symbol)?.served} />
                  </td>
                  <td style={TD}>
                    <Cell verdict={symbolIn(b, row.symbol)?.verdict}
                          date={symbolIn(b, row.symbol)?.served} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <div style={SECTION}>Every file</div>
      <table style={TABLE}>
        <thead>
          <tr>
            <th style={TH}>file</th>
            <th style={TH}>rule</th>
            <th style={TH}>written</th>
            <th style={TH}>Rpt 1</th>
            <th style={TH}>Rpt 2</th>
          </tr>
        </thead>
        <tbody>
          {paths.map((path) => {
            const rowA = fileIn(a, path);
            const rowB = fileIn(b, path);
            return (
              <tr key={path}>
                <td style={TD}>
                  {plainFileName(path)}
                  {rowA && !rowA.registered && (
                    <span style={{ color: "var(--qf-text-secondary)" }}> unregistered</span>
                  )}
                </td>
                <td style={{ ...TD, color: "var(--qf-text-secondary)" }}>
                  {rowA?.rule ?? "—"}
                </td>
                <td style={{ ...TD, color: "var(--qf-text-secondary)" }}>
                  {ago(rowA?.base?.written)}
                </td>
                <td style={TD}>
                  <Cell verdict={rowA?.verdict} date={rowA?.served?.lastDate} />
                </td>
                <td style={TD}>
                  <Cell verdict={rowB?.verdict} date={rowB?.served?.lastDate} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}
